import createClient from "openapi-fetch";
import type { paths as CatalogPaths } from "./catalog";
import type { paths as IdentityPaths } from "./identity";
import type { paths as CustomersPaths } from "./customers";
import type { paths as OrderingPaths } from "./ordering";
import type { paths as BillingPaths } from "./billing";
import type { paths as TallySyncPaths } from "./tallysync";
import type { paths as WarehousePaths } from "./warehouse";

/**
 * The generated clients (FR-003), one per reachable backend service. Hand-written fetch to a
 * backend route is prohibited; ESLint enforces that in every package.
 *
 * ## Every factory takes the gateway ROOT, and none of them appends a service prefix
 *
 * The generated `paths` keys already carry the full `/api/<service>/...` prefix, because that is
 * what each service publishes and what the gateway forwards **unchanged** — the backend's
 * `appsettings.json` says so in a comment and deliberately sets no `PathRemovePrefix`. Appending
 * a prefix here would produce `/api/catalog/api/catalog/brands`.
 *
 * This matters most for **ordering**, which is the one service whose document name and route
 * prefix differ: it publishes `/openapi/ordering.json` but is routed at **`/api/orders`**. A
 * factory that built its base URL from the service name would send every order request to
 * `/api/ordering/...` and get a 404 from the gateway. Nothing here builds a URL from a name; the
 * document is the only source of the paths.
 *
 * ## What these type, and what they do not
 *
 * Routes, path/query parameters and **request bodies** are checked against the documents: a
 * renamed route or a changed request shape breaks `pnpm type:check`, which is what FR-003 asks
 * for.
 *
 * **Response bodies are typed only where the service declares them**, and coverage is uneven —
 * measured at the 2026-08-31 snapshot, as operations with at least one response declaring
 * `content`:
 *
 * | Service | Operations | With a typed response |
 * |---|---|---|
 * | identity | 31 | **0** |
 * | catalog | 50 | 23 |
 * | customers | 24 | 7 |
 * | ordering | 29 | 9 |
 * | billing | 35 | 33 |
 * | tallysync | 14 | 7 |
 *
 * Where a response is undeclared it generates as `undefined`, so `data.items` will not compile.
 * Do **not** reach for `as unknown as HandWrittenType` — that is the second copy of the contract
 * that FR-003 exists to prevent. See `specs/001-catalogue-admin-screens/contracts/backend-gaps.md`
 * GAP-4; it is partially closed (catalog and billing now declare most of theirs) and identity
 * still declares none.
 */
export function createCatalogClient(baseUrl: string) {
  return createClient<CatalogPaths>({ baseUrl });
}

export function createIdentityClient(baseUrl: string) {
  return createClient<IdentityPaths>({ baseUrl });
}

export function createCustomersClient(baseUrl: string) {
  return createClient<CustomersPaths>({ baseUrl });
}

/** Routed at `/api/orders`, not `/api/ordering`. See the note above. */
export function createOrderingClient(baseUrl: string) {
  return createClient<OrderingPaths>({ baseUrl });
}

export function createBillingClient(baseUrl: string) {
  return createClient<BillingPaths>({ baseUrl });
}

export function createTallySyncClient(baseUrl: string) {
  return createClient<TallySyncPaths>({ baseUrl });
}

/** Routed at `/api/warehouse` — serves both `005` (packing) and `007` (inventory). One client, one service. */
export function createWarehouseClient(baseUrl: string) {
  return createClient<WarehousePaths>({ baseUrl });
}

/**
 * Catalog's types keep the unqualified names they were exported under when it was the only
 * service. Every other service is namespaced, because six services cannot all export `paths`.
 */
export type { paths, components } from "./catalog";
export type { paths as CatalogPaths, components as CatalogComponents } from "./catalog";
export type { paths as IdentityPaths, components as IdentityComponents } from "./identity";
export type { paths as CustomersPaths, components as CustomersComponents } from "./customers";
export type { paths as OrderingPaths, components as OrderingComponents } from "./ordering";
export type { paths as BillingPaths, components as BillingComponents } from "./billing";
export type { paths as TallySyncPaths, components as TallySyncComponents } from "./tallysync";
export type { paths as WarehousePaths, components as WarehouseComponents } from "./warehouse";

// The session helpers (readTokens, writeTokens, createSessionMiddleware, ...) are not part of this copy:
// apps/admin never imported them - its BFF owns the session - and they stay with the portal.
